import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Button, Input, Row, Col, Space, Table, Tag, message, Tooltip, Grid, List, Dropdown, Menu } from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined, EyeOutlined, DownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { Receivable, ReceivableStatus, ReceivableType } from '@/types/finance';
import { getReceivables, addReceivable, updateReceivable, deleteReceivable, createBulkReceivables } from '@/api/finance';
import { getOccupiedApartmentUnits } from '@/api/properties';
import { useAuth } from '@/contexts/AuthContext';
import { Money } from '@/types';
import ReceivableModal from '../../components/features/finance/ReceivableModal';
import DeleteConfirmationModal from '../../components/common/DeleteConfirmationModal';
import BulkReceivableModal, { BulkReceivableData } from '../../components/features/finance/BulkReceivableModal';
import dayjs from 'dayjs';
import { formatFirestoreTimestamp } from '@/utils/date';

const PAGE_SIZE = 10;

const Receivables: React.FC = () => {
  const { t } = useTranslation(['receivables', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReceivable, setEditingReceivable] = useState<Receivable | null>(null);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [receivableToDelete, setReceivableToDelete] = useState<Receivable | null>(null);
  const [bulkModalVisible, setBulkModalVisible] = useState(false);
  const [occupiedUnits, setOccupiedUnits] = useState<any[]>([]);
  
  // Queries
  const { data, isLoading } = useQuery({
    queryKey: ['receivables', page, searchTerm],
    queryFn: async () => {
      return getReceivables({
        searchTerm,
        pageSize: PAGE_SIZE,
        page,
        firestoreUser: firestoreUser!,
      });
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
    enabled: !!firestoreUser,
  });

  // Mutations
  const addMutation = useMutation({
    mutationFn: (receivable: Omit<Receivable, 'id' | 'activityRecords' | 'workspace'>) =>
      addReceivable(receivable, firestoreUser!),
    onSuccess: () => {
      message.success(t('receivables:messages.createSuccess'));
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      handleModalCancel();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Receivable> }) =>
      updateReceivable(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('receivables:messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      handleModalCancel();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteReceivable(id, firestoreUser!),
    onSuccess: () => {
      message.success(t('receivables:messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      setDeleteModalVisible(false);
      setReceivableToDelete(null);
    },
    onError: () => {
      message.error(t('receivables:messages.deleteError'));
    },
  });

  const bulkCreateMutation = useMutation({
    mutationFn: (receivables: Omit<Receivable, 'id' | 'activityRecords' | 'workspace'>[]) =>
      createBulkReceivables(receivables, firestoreUser!),
    onSuccess: (result) => {
      message.success(t('receivables:messages.bulkCreateSuccess', { count: result.successCount }));
      queryClient.invalidateQueries({ queryKey: ['receivables'] });
      setBulkModalVisible(false);
      if (result.errorCount > 0) {
        message.warning(`${result.errorCount} receivables failed to create`);
      }
    },
    onError: () => {
      message.error(t('receivables:messages.bulkCreateError'));
    },
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingReceivable(null);
    setIsModalOpen(true);
  }, []);

  const handleEdit = useCallback((record: Receivable) => {
    setEditingReceivable(record);
    setIsModalOpen(true);
  }, []);

  const handleView = useCallback((record: Receivable) => {
    navigate(`/finance/receivables/${record.id}`, { state: { receivable: record } });
  }, [navigate]);

  const handleDelete = useCallback((record: Receivable) => {
    setReceivableToDelete(record);
    setDeleteModalVisible(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (receivableToDelete) {
      deleteMutation.mutate(receivableToDelete.id);
    }
  }, [receivableToDelete, deleteMutation]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteModalVisible(false);
    setReceivableToDelete(null);
  }, []);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingReceivable(null);
  }, []);

  const handleModalSuccess = useCallback(async (receivableData: Omit<Receivable, 'id' | 'activityRecords' | 'workspace'>) => {
    if (editingReceivable) {
      await updateMutation.mutateAsync({
        id: editingReceivable.id,
        data: receivableData,
      });
    } else {
      await addMutation.mutateAsync(receivableData);
    }
  }, [editingReceivable, addMutation, updateMutation]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  }, []);

  const handleBulkGenerate = useCallback(async (_type: 'common' | 'parking' | 'apartment') => {
    try {
      const units = await getOccupiedApartmentUnits(firestoreUser!);
      setOccupiedUnits(units.apartmentUnits);
      setBulkModalVisible(true);
    } catch (error) {
      message.error('Failed to load occupied units');
    }
  }, [firestoreUser]);

  const handleBulkModalCancel = useCallback(() => {
    setBulkModalVisible(false);
    setOccupiedUnits([]);
  }, []);

  const handleBulkSuccess = useCallback(async (data: BulkReceivableData) => {
    const receivables = data.residentReceivables.map(rr => ({
      name: `${data.name} - ${rr.residentName}`,
      type: data.type,
      status: 'pending' as const,
      amount: {
        amount: rr.amount,
        currency: 'USD'
      },
      dueDate: data.dueDate.toDate(),
      description: data.description,
      resident: {
        id: rr.residentId,
        name: rr.residentName
      }
    }));

    await bulkCreateMutation.mutateAsync(receivables);
  }, [bulkCreateMutation]);

  // Memoized values
  const filteredData = useMemo(() => {
    return data?.receivables || [];
  }, [data]);

  const getStatusTagColor = (status: ReceivableStatus) => {
    switch (status) {
      case 'pending': return 'orange';
      case 'partial': return 'blue';
      case 'paid': return 'green';
      case 'overdue': return 'red';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getTypeTagColor = (type: ReceivableType) => {
    switch (type) {
      case 'rent': return 'blue';
      case 'maintenance': return 'orange';
      case 'utility': return 'green';
      case 'fine': return 'red';
      case 'other': return 'default';
      default: return 'default';
    }
  };

  const formatMoney = (amount: Money) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: amount.currency || 'USD'
    }).format(amount.amount);
  };

  const getTypeLabel = (type: ReceivableType) => {
    switch (type) {
      case 'rent': return 'Rent';
      case 'maintenance': return 'Maintenance';
      case 'utility': return 'Utility';
      case 'fine': return 'Fine';
      case 'other': return 'Other';
      default: return 'Other';
    }
  };

  const getStatusLabel = (status: ReceivableStatus) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'partial': return 'Partially Paid';
      case 'paid': return 'Paid';
      case 'overdue': return 'Overdue';
      case 'cancelled': return 'Cancelled';
      default: return 'Pending';
    }
  };

  const renderReceivableCard = (receivable: Receivable) => (
    <Card
      key={receivable.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleView(receivable)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(receivable)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(receivable)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={receivable.name}
        description={
          <Space direction="vertical" size="small">
            <div>
              <Tag color={getTypeTagColor(receivable.type)}>
                {getTypeLabel(receivable.type)}
              </Tag>
              <Tag color={getStatusTagColor(receivable.status)}>
                {getStatusLabel(receivable.status)}
              </Tag>
            </div>
            <div>
              <strong>{t('common:amount')}:</strong> {formatMoney(receivable.amount)}
            </div>
            <div>
              <strong>{t('common:dueDate')}:</strong> {
                (() => {
                  const formattedDate = formatFirestoreTimestamp(receivable.dueDate);
                  return formattedDate ? dayjs(formattedDate).format('MMM DD, YYYY') : 'Invalid date';
                })()
              }
            </div>
          </Space>
        }
      />
    </Card>
  );

  const receivableTypes = [
    { text: 'Rent', value: 'rent' },
    { text: 'Maintenance', value: 'maintenance' },
    { text: 'Utility', value: 'utility' },
    { text: 'Fine', value: 'fine' },
    { text: 'Other', value: 'other' },
  ];

  const receivableStatuses = [
    { text: 'Pending', value: 'pending' },
    { text: 'Partially Paid', value: 'partial' },
    { text: 'Paid', value: 'paid' },
    { text: 'Overdue', value: 'overdue' },
    { text: 'Cancelled', value: 'cancelled' },
  ];

  const generateMenu = (
    <Menu
      onClick={({ key }) => handleBulkGenerate(key as 'common' | 'parking' | 'apartment')}
      items={[
        {
          key: 'common',
          label: t('receivables:bulkGeneration.commonExpenses'),
        },
        {
          key: 'parking',
          label: t('receivables:bulkGeneration.parkingRentals'),
          disabled: true, // Not implemented yet
        },
        {
          key: 'apartment',
          label: t('receivables:bulkGeneration.apartmentRentals'),
          disabled: true, // Not implemented yet
        },
      ]}
    />
  );

  const columns = [
    {
      title: t('common:name'),
      dataIndex: 'name',
      key: 'name',
      sorter: (a: Receivable, b: Receivable) => a.name.localeCompare(b.name),
      render: (text: string) => <span style={{ fontWeight: 500 }}>{text}</span>,
    },
    {
      title: t('common:typeLabel'),
      dataIndex: 'type',
      key: 'type',
      filters: receivableTypes,
      render: (type: ReceivableType) => (
        <Tag color={getTypeTagColor(type)}>
          {getTypeLabel(type)}
        </Tag>
      ),
    },
    {
      title: t('common:amount'),
      dataIndex: 'amount',
      key: 'amount',
      sorter: (a: Receivable, b: Receivable) => a.amount.amount - b.amount.amount,
      render: (amount: Money) => (
        <span style={{ fontWeight: 500, color: '#52c41a' }}>
          {formatMoney(amount)}
        </span>
      ),
    },
    {
      title: t('common:dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      sorter: (a: Receivable, b: Receivable) => {
        const dateA = formatFirestoreTimestamp(a.dueDate);
        const dateB = formatFirestoreTimestamp(b.dueDate);
        if (!dateA || !dateB) return 0;
        return dayjs(dateA).unix() - dayjs(dateB).unix();
      },
      render: (date: any) => {
        const formattedDate = formatFirestoreTimestamp(date);
        if (!formattedDate) return 'Invalid date';
        return dayjs(formattedDate).format('MMM DD, YYYY');
      },
    },
    {
      title: t('common:statusLabel'),
      dataIndex: 'status',
      key: 'status',
      filters: receivableStatuses,
      render: (status: ReceivableStatus) => (
        <Tag color={getStatusTagColor(status)}>
          {getStatusLabel(status)}
        </Tag>
      ),
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Receivable) => (
        <Space size="small">
          <Tooltip title={t('common:details')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title={t('common:actions.edit')}>
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title={t('common:actions.delete')}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              size="small"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Typography.Title level={3}>{t('receivables:title')}</Typography.Title>
        </Col>
        <Col>
          <Space direction={screens.xs ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input
              placeholder={t('receivables:searchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
              prefix={<SearchOutlined />}
              style={{ width: screens.xs ? '100%' : 300 }}
            />
            <Dropdown overlay={generateMenu} trigger={['click']}>
              <Button icon={<PlusOutlined />}>
                {t('receivables:bulkGeneration.generateButton')} <DownOutlined />
              </Button>
            </Dropdown>
            <Button type="primary" onClick={handleAdd} icon={<PlusOutlined />}>
              {t('common:actions.add')}
            </Button>
          </Space>
        </Col>
      </Row>
      
      {/* Table */}
      <Card>
        {screens.xs ? (
          <List
            dataSource={filteredData}
            renderItem={renderReceivableCard}
            rowKey="id"
            loading={isLoading}
            grid={{ gutter: 16, xs: 1, sm: 1 }}
          />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            loading={isLoading}
            rowKey="id"
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: data?.total || 0,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                t('common:showingRange', { from: range[0], to: range[1], total }),
              onChange: (newPage) => {
                setPage(newPage);
              }
            }}
            scroll={{ x: 800 }}
          />
        )}
      </Card>
      
      {/* Modal */}
      <ReceivableModal
        open={isModalOpen}
        receivable={editingReceivable}
        onSuccess={handleModalSuccess}
        onCancel={handleModalCancel}
        loading={addMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onCancel={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t('receivables:messages.deleteConfirmTitle', { name: receivableToDelete?.name })}
        error={deleteMutation.error as any}
        onErrorDismiss={() => setDeleteModalVisible(false)}
      />

      {/* Bulk Receivable Modal */}
      <BulkReceivableModal
        open={bulkModalVisible}
        onCancel={handleBulkModalCancel}
        onSuccess={handleBulkSuccess}
        loading={bulkCreateMutation.isPending}
        occupiedUnits={occupiedUnits}
      />
    </div>
  );
};

export default Receivables; 